import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild, OnInit, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { DirectorSearchFilter } from '../../../models/directorSearchFilter';
import { Observable, of, forkJoin } from 'rxjs';
import { map, tap, shareReplay, take } from 'rxjs/operators';
import { Nationality } from '../../../models/nationality';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { MaterialModule } from '../../../shared/material.module';
import { Profession } from '../../../models/profession';
import { HonorificTitle } from '../../../models/honorificTitle';
import { Qualification } from '../../../models/qualification';
import { FinancialExpertise } from '../../../models/financialExpertise';
import { PoliticalPosition } from '../../../models/politicalPosition';
import { UserService } from '../../../services/user.service';
import { AppUserBase } from '../../../models/appUserBase';
import { CountryFlat } from '../../../models/country';
import { LutService } from '../../../services/lut.service';
import { Helpers } from '../../../utilities/helpers';
import { REGIONS, RegionOption } from '../../../utilities/regions';
import { BoardMemberOccupation } from '../../../models/boardMemberOccupation';
import { Industry, IndustryFlat } from '../../../models/industry';
import { TreeSelectComponent } from '../../shared/tree-select/tree-select.component';
import { TextNormalizeDirective } from '../../../directives/text-normalize.directive';

export interface DirectorSearchFieldsConfig {
  showCode?: boolean;
  showFullName?: boolean;
  showAgeFrom?: boolean;
  showAgeTo?: boolean;
  showGender?: boolean;
  showNationality?: boolean;
  showDeceased?: boolean;
  showProfession?: boolean;
  showIsNotCompleted?: boolean;
  showCreatedBy?: boolean;
  showDateFrom?: boolean;
  showDateTo?: boolean;
  showRegion?: boolean;
  showCountry?: boolean;
  showYear?: boolean;
  showCompany?: boolean;
  showBoardMemberOccupation?: boolean;
  showClassification?: boolean;
  showIndustry?: boolean;
  showMembershipType?: boolean;
  showTenure?: boolean;
  showAdminTab?: boolean;
}

@Component({
  selector: 'app-director-search-box',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    TreeSelectComponent,
    TextNormalizeDirective
],
  templateUrl: './director-search-box.component.html',
  styleUrl: './director-search-box.component.scss'
})
export class DirectorSearchBoxComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() isSearchVisible: boolean = false;
  @Input() initialFilter: DirectorSearchFilter = {};
  @Input() fieldsConfig: DirectorSearchFieldsConfig = {
    showCode: true,
    showFullName: true,
    showAgeFrom: true,
    showAgeTo: true,
    showGender: true,
    showNationality: true,
    showDeceased: true,
    showProfession: true,
    showIsNotCompleted: true,
    showCreatedBy: true,
    showDateFrom: true,
    showDateTo: true,
    showRegion: true,
    showCountry: true,
    showYear: true,
    showCompany: true,
    showBoardMemberOccupation: true,
    showClassification: true,
    showIndustry: true,
    showMembershipType: true,
    showTenure: true,
    showAdminTab: true
  };
  @Output() directorSearchFilter = new EventEmitter<DirectorSearchFilter>();
  @Output() showBoardMembershipsChange = new EventEmitter<boolean>();

  showBoardMemberships = false;
  @ViewChild(TreeSelectComponent) treeSelectComponent!: TreeSelectComponent<Industry>;

  regions: RegionOption[] = REGIONS;
  years: string[] = [];
  classificationOptions = [
    { value: 1, label: 'Executive' },
    { value: 2, label: 'Independent' },
    { value: 3, label: 'Non-Executive' },
    { value: 4, label: 'Null or Unknown' }
  ];
  membershipTypeOptions = [
    { value: 0, label: 'All' },
    { value: 1, label: 'Directors' },
    { value: 2, label: 'Officers' }
  ];

  nationalities$: Observable<Nationality[] | null>;
  professions$: Observable<Profession[] | null>;
  honorificTitles$: Observable<HonorificTitle[] | null>;
  qualifications$: Observable<Qualification[] | null>;
  financialExpertises$: Observable<FinancialExpertise[] | null>;
  politicalPositions$: Observable<PoliticalPosition[] | null>;
  users$: Observable<AppUserBase[]> = of([]);
  countries$!: Observable<CountryFlat[]>;
  allCountries: CountryFlat[] = [];
  boardMemberOccupations$: Observable<BoardMemberOccupation[] | null>;
  industries$!: Observable<Industry[]>;

  searchForm: {
    code: string | undefined;
    fullName: string | undefined;
    ageFrom: number | undefined;
    ageTo: number | undefined;
    gender: number | undefined;
    nationalityId: string | undefined;
    isDeceased: boolean | undefined;
    professionId: string | undefined;
    profileStatus: string;
    createdBy: string | undefined;
    dateFrom: string | undefined;
    dateTo: string | undefined;
    region: number | undefined;
    countryId: string | undefined;
    year: string | undefined;
    company: string | undefined;
    boardMemberOccupationId: string | undefined;
    classification: number | undefined;
    industryId: string | undefined;
    membershipType: number;
    tenureMin: number | undefined;
    tenureMax: number | undefined;
  } = {
    code: undefined,
    fullName: undefined,
    ageFrom: undefined,
    ageTo: undefined,
    gender: undefined,
    nationalityId: undefined,
    isDeceased: undefined,
    professionId: undefined,
    profileStatus: 'all',
    createdBy: undefined,
    dateFrom: undefined,
    dateTo: undefined,
    region: undefined,
    countryId: undefined,
    year: undefined,
    company: undefined,
    boardMemberOccupationId: undefined,
    classification: undefined,
    industryId: undefined,
    membershipType: 0,
    tenureMin: undefined,
    tenureMax: undefined
  };

  constructor(private lookupService: LookupDataService, private userService: UserService, private lutService: LutService) {
    this.nationalities$ = this.lookupService.getTableStream<Nationality>('nationalities');
    this.professions$ = this.lookupService.getTableStream<Profession>('professions');
    this.honorificTitles$ = this.lookupService.getTableStream<HonorificTitle>('honorificTitles');
    this.qualifications$ = this.lookupService.getTableStream<Qualification>('qualifications');
    this.financialExpertises$ = this.lookupService.getTableStream<FinancialExpertise>('financialExpertises');
    this.politicalPositions$ = this.lookupService.getTableStream<PoliticalPosition>('politicalPositions');
    this.boardMemberOccupations$ = this.lookupService.getTableStream<BoardMemberOccupation>('boardMemberOccupations');
    this.industries$ = this.lookupService.getTableStream<IndustryFlat>('industries').pipe(
      map(flatIndustries => {
        if (!flatIndustries) return [];
        const hierarchical = Helpers.convertFlatToHierarchical(flatIndustries);
        return Helpers.sortIndustriesAlphabetically(hierarchical);
      })
    );
    this.years = Helpers.measurements();
  }

  ngOnInit(): void {
    // The "Created By" dropdown lives in the Admin tab and hits an admin-only endpoint —
    // only fetch users when that tab is actually shown.
    if (this.fieldsConfig.showAdminTab !== false && this.fieldsConfig.showCreatedBy !== false) {
      this.users$ = this.userService.getUsers(1, 100, 'FullName', true).pipe(
        map(response => response.data)
      );
    }

    // Load countries from cache
    const countriesData$ = this.lookupService.getVisibleCountries<CountryFlat>().pipe(
      tap(countries => {
        this.allCountries = countries;
      }),
      shareReplay(1)
    );

    // Wait for countries to load before populating the search form
    countriesData$.subscribe(() => {
      this.populateSearchForm();
    });

    // Set up the countries observable for the template
    this.countries$ = countriesData$.pipe(
      map(countries => countries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })))
    );
  }

  ngAfterViewInit(): void {
    // Restore selected industry after view is initialized
    this.restoreSelectedIndustry();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialFilter'] && !changes['initialFilter'].firstChange) {
      this.populateSearchForm();
      // Also restore the industry selection when filter changes
      setTimeout(() => this.restoreSelectedIndustry(), 0);
    }
  }

  private populateSearchForm(): void {
    if (this.initialFilter && Object.keys(this.initialFilter).length > 0) {
      this.searchForm = {
        code: this.initialFilter.code,
        fullName: this.initialFilter.fullName,
        ageFrom: this.initialFilter.ageFrom,
        ageTo: this.initialFilter.ageTo,
        gender: this.initialFilter.gender,
        nationalityId: this.initialFilter.nationalityId,
        isDeceased: this.initialFilter.isDeceased,
        professionId: this.initialFilter.professionId,
        profileStatus: this.initialFilter.isProfileFinished === true ? 'completed' : this.initialFilter.isProfileFinished === false ? 'notCompleted' : 'all',
        createdBy: this.initialFilter.createdBy,
        dateFrom: this.initialFilter.dateFrom,
        dateTo: this.initialFilter.dateTo,
        region: this.initialFilter.region,
        countryId: this.initialFilter.countryId,
        year: this.initialFilter.year,
        company: this.initialFilter.company,
        boardMemberOccupationId: this.initialFilter.boardMemberOccupationId,
        classification: this.initialFilter.classification,
        industryId: this.initialFilter.industryId,
        membershipType: this.initialFilter.membershipType ?? 0,
        tenureMin: this.initialFilter.tenureMin,
        tenureMax: this.initialFilter.tenureMax
      };

      // Filter countries if region is present
      if (this.initialFilter.region) {
        this.countries$ = of(
          this.allCountries
            .filter(country => country.region === this.initialFilter.region)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        );
      }
    }
  }

  private restoreSelectedIndustry(): void {
    // Set the selected industry on the tree-select component
    if (this.searchForm.industryId && this.treeSelectComponent) {
      this.industries$.pipe(take(1)).subscribe(industries => {
        if (industries && industries.length > 0) {
          const selectedIndustry = this.findIndustryById(industries, this.searchForm.industryId!);
          if (selectedIndustry && this.treeSelectComponent) {
            this.treeSelectComponent.selectedNode = selectedIndustry;
          }
        }
      });
    }
  }

  private findIndustryById(industries: Industry[], id: string): Industry | null {
    for (const industry of industries) {
      if (industry.id === id) {
        return industry;
      }
      if (industry.children && industry.children.length > 0) {
        const found = this.findIndustryById(industry.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  onRegionChange(): void {
    if (this.searchForm.region) {
      this.countries$ = of(
        this.allCountries
          .filter(country => country.region === this.searchForm.region)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      );
    } else {
      this.countries$ = of(
        this.allCountries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      );
      this.searchForm.countryId = undefined;
    }
  }

  onIndustrySelectionChange(selectedIndustry: any): void {
    this.searchForm.industryId = selectedIndustry ? selectedIndustry.id : undefined;
  }

  onShowBoardMembershipsChange(): void {
    this.showBoardMembershipsChange.emit(this.showBoardMemberships);
  }

  onSearch(): void {
    // Convert searchForm to DirectorSearchFilter format
    const filter: DirectorSearchFilter = {
      code: this.searchForm.code,
      fullName: this.searchForm.fullName,
      ageFrom: this.searchForm.ageFrom,
      ageTo: this.searchForm.ageTo,
      gender: this.searchForm.gender,
      nationalityId: this.searchForm.nationalityId,
      isDeceased: this.searchForm.isDeceased === true ? true : undefined,
      professionId: this.searchForm.professionId,
      isProfileFinished: this.searchForm.profileStatus === 'completed' ? true : this.searchForm.profileStatus === 'notCompleted' ? false : undefined,
      createdBy: this.searchForm.createdBy,
      dateFrom: this.searchForm.dateFrom ? new Date(this.searchForm.dateFrom).toISOString() : undefined,
      dateTo: this.searchForm.dateTo ? new Date(this.searchForm.dateTo).toISOString() : undefined,
      region: this.searchForm.region,
      countryId: this.searchForm.countryId,
      year: this.searchForm.year,
      company: this.searchForm.company,
      boardMemberOccupationId: this.searchForm.boardMemberOccupationId,
      classification: this.searchForm.classification,
      industryId: this.searchForm.industryId,
      membershipType: this.searchForm.membershipType !== 0 ? this.searchForm.membershipType : undefined,
      tenureMin: this.searchForm.tenureMin != null ? Number(this.searchForm.tenureMin) : undefined,
      tenureMax: this.searchForm.tenureMax != null ? Number(this.searchForm.tenureMax) : undefined
    };
    this.directorSearchFilter.emit(filter);
  }

  clearSearch(): void {
    this.searchForm = {
      code: undefined,
      fullName: undefined,
      ageFrom: undefined,
      ageTo: undefined,
      gender: undefined,
      nationalityId: undefined,
      isDeceased: undefined,
      professionId: undefined,
      profileStatus: 'all',
      createdBy: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      region: undefined,
      countryId: undefined,
      year: undefined,
      company: undefined,
      boardMemberOccupationId: undefined,
      classification: undefined,
      industryId: undefined,
      membershipType: 0,
      tenureMin: undefined,
      tenureMax: undefined
    };
    this.countries$ = of(
      this.allCountries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    );
    // Clear the tree-select component selection
    if (this.treeSelectComponent) {
      this.treeSelectComponent.clearSelection();
    }
    // Emit empty filter
    this.directorSearchFilter.emit({});
  }

  get personalCharacteristicsCount(): number {
    let count = 0;
    if (this.searchForm.code) count++;
    if (this.searchForm.fullName) count++;
    if (this.searchForm.ageFrom) count++;
    if (this.searchForm.ageTo) count++;
    if (this.searchForm.gender !== undefined && this.searchForm.gender !== null) count++;
    if (this.searchForm.nationalityId) count++;
    if (this.searchForm.professionId) count++;
    return count;
  }

  get generalCount(): number {
    let count = 0;
    if (this.searchForm.region) count++;
    if (this.searchForm.countryId) count++;
    if (this.searchForm.year) count++;
    return count;
  }

  get workExperienceCount(): number {
    let count = 0;
    if (this.searchForm.membershipType) count++;
    if (this.searchForm.company) count++;
    if (this.searchForm.boardMemberOccupationId) count++;
    if (this.searchForm.classification) count++;
    if (this.searchForm.industryId) count++;
    if (this.searchForm.tenureMin != null) count++;
    if (this.searchForm.tenureMax != null) count++;
    return count;
  }

  get adminCount(): number {
    let count = 0;
    if (this.searchForm.createdBy) count++;
    if (this.searchForm.dateFrom) count++;
    if (this.searchForm.dateTo) count++;
    if (this.searchForm.isDeceased) count++;
    if (this.searchForm.profileStatus && this.searchForm.profileStatus !== 'all') count++;
    return count;
  }
}
